import { getPreferences } from "@/lib/preferences";
import { currencySymbol } from "@/lib/currency";

export function money(value: unknown, code?: string | null): string {
  const n = Number(value ?? 0);
  const symbol = currencySymbol(code);
  return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDatePart(d: Date): string {
  const f = getPreferences().dateFormat;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  if (f === "dmy") return `${day}/${m}/${y}`;
  if (f === "ymd") return `${y}-${m}-${day}`;
  return `${m}/${day}/${y}`;
}

export function fmtDate(value: unknown): string {
  if (!value) return "—";
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDatePart(d);
}

export function fmtDateTime(value: unknown): string {
  if (!value) return "—";
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return "—";
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${formatDatePart(d)} ${time}`;
}

export function initials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function variantLabel(optionValues: unknown): string {
  if (!optionValues || typeof optionValues !== "object") return "";
  const entries = Object.entries(optionValues as Record<string, unknown>);
  if (entries.length === 0) return "";
  return entries.map(([, v]) => String(v)).filter(Boolean).join(" / ");
}

/**
 * Turn a machine identifier (snake_case / camelCase / kebab) into a
 * human-readable label. Used as a defensive fallback so raw statuses never
 * leak to the UI.
 */
export function humanize(value: unknown): string {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Operator-facing label for a status value. Mirrors the canonical label used by
 * `StatusBadge` for known statuses (so a filter dropdown and a badge never
 * disagree) and falls back to `humanize` for anything else. Pure display only —
 * the raw value is never changed.
 */
const STATUS_LABEL_OVERRIDES: Record<string, string> = {
  pending_carrier: "Awaiting Carrier",
};

export function statusLabel(value: unknown): string {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return STATUS_LABEL_OVERRIDES[s] ?? humanize(s);
}

/**
 * Human label for a marketplace/connector reconciliation mode. The raw value
 * (LIVE / DRY_RUN / NOT_CONFIGURED) is the canonical storage/filter value and is
 * never changed — this is presentation only.
 */
export function reconciliationModeLabel(value: unknown): string {
  const s = String(value ?? "").trim();
  switch (s) {
    case "LIVE":
      return "Live";
    case "DRY_RUN":
      return "Test mode";
    case "NOT_CONFIGURED":
      return "Not configured";
    default:
      return humanize(s);
  }
}